import React, { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckCircle2, XCircle, Building2, UserCheck, Clock, Trash2, Search, EyeOff, Mail, Languages, Handshake, Bell, Pencil, Folder, ToggleRight, Pause, Power } from "lucide-react";
import { formatMoney, t } from "@/i18n";
import { useApp } from "@/context/AppContext";
import MandateCard from "@/components/mandates/MandateCard";
import ContactInbox from "@/components/admin/ContactInbox";
import AdminUserEditDialog from "@/components/admin/AdminUserEditDialog";
import AdminUserDocsDialog from "@/components/admin/AdminUserDocsDialog";
import { logWarn } from "@/lib/logger";

const DEFAULT_REASON = "Contatta l'amministratore su info@aaronproperties.it";

const STATUS_META = {
  pending:  { label: "IN ATTESA", cls: "bg-amber-100 text-amber-800 border border-amber-200", Icon: Clock },
  approved: { label: "PUBBLICATA", cls: "bg-emerald-100 text-emerald-800 border border-emerald-200", Icon: CheckCircle2 },
  rejected: { label: "RIFIUTATA",  cls: "bg-red-100 text-red-800 border border-red-200", Icon: XCircle },
};

const FILTERS = [
  { key: "all", label: "Tutte" },
  { key: "pending", label: "In attesa" },
  { key: "approved", label: "Pubblicate" },
  { key: "rejected", label: "Rifiutate" },
];

function RejectDialog({ open, target, onClose, onSubmit }) {
  const [reason, setReason] = useState(DEFAULT_REASON);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setReason(DEFAULT_REASON); }, [open]);
  const submit = async () => {
    setBusy(true);
    try { await onSubmit(reason); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white border-[#E5E5E3] max-w-md" data-testid="admin-reject-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-light text-[#1A1A1A]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            Motivo del rifiuto
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#6B6B6B]">
          Sarà inviato via email a {target?.name || "l'utente"}.
        </p>
        <Textarea
          data-testid="admin-reject-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          className="border-[#E5E5E3] bg-[#FAFAF8]"
        />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-[#6B6B6B]">Annulla</Button>
          <Button
            onClick={submit}
            disabled={busy || !reason.trim()}
            data-testid="admin-reject-submit"
            className="bg-red-700 hover:bg-red-800 text-white rounded-full"
          >
            {busy ? "…" : "Rifiuta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkTranslatePanel() {
  const { lang } = useApp();
  const [target, setTarget] = useState("en");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const labels = {
    it: { title: "Traduci tutti i mandati", helper: "Pre-riscalda la cache traducendo tutti i mandati nella lingua scelta.", run: "Avvia", running: "In corso…" },
    en: { title: "Translate all mandates", helper: "Pre-warm the cache by translating every mandate PDF into the selected language.", run: "Run", running: "Running…" },
    he: { title: "תרגם את כל המנדטים", helper: "חמם את המטמון ותרגם את כל המנדטים לשפה הנבחרת.", run: "הפעל", running: "פועל…" },
    ru: { title: "Перевести все мандаты", helper: "Прогреть кэш переводов для всех мандатов в выбранный язык.", run: "Запустить", running: "Выполняется…" },
  };
  const L = labels[lang] || labels.en;
  const submit = async () => {
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("target_lang", target);
      const { data } = await api.post("/admin/documents/translate-all-mandates", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000, // up to 10 minutes for the whole batch
      });
      setResult(data);
      toast.success(`Translated ${data.translated_now} · Cached ${data.already_cached} · Failed ${data.failed}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-lg border border-[#D8CFBB] bg-[#FBF8F1] p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="admin-bulk-translate">
      <div className="flex-1 min-w-0">
        <p className="text-xs tracking-[0.2em] uppercase text-[#B8965A] mb-0.5">{L.title}</p>
        <p className="text-xs text-[#7A6E5A]">{L.helper}</p>
        {result && (
          <p className="text-[11px] text-emerald-700 mt-1" data-testid="admin-bulk-translate-result">
            +{result.translated_now} translated · {result.already_cached} cached · {result.failed} failed / {result.total_mandates}
          </p>
        )}
      </div>
      <select
        value={target}
        onChange={(e) => setTarget(e.target.value)}
        disabled={busy}
        data-testid="admin-bulk-translate-target"
        className="text-xs px-3 py-1.5 rounded-full border border-[#D8CFBB] bg-white text-[#1A1A1A]"
      >
        <option value="it">Italiano</option>
        <option value="en">English</option>
        <option value="he">עברית</option>
        <option value="ru">Русский</option>
      </select>
      <Button
        size="sm"
        onClick={submit}
        disabled={busy}
        data-testid="admin-bulk-translate-run"
        className="bg-[#B8965A] hover:bg-[#a08248] text-white rounded-full text-xs h-8 disabled:opacity-50"
      >
        <Languages className="h-3 w-3 me-1" />
        {busy ? L.running : L.run}
      </Button>
    </div>
  );
}

function WipeDemoDataPanel() {
  const { lang } = useApp();
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const L = {
    it: { title: "Cancella dati di test", body: "Elimina tutti gli utenti di test e i loro contenuti. I membri reali restano intatti.", scan: "Scansiona", commit: "Cancella definitivamente", back: "Annulla", scanning: "…" },
    en: { title: "Wipe demo data", body: "Delete all test users and their content. Real members are preserved.", scan: "Scan", commit: "Delete permanently", back: "Cancel", scanning: "…" },
    he: { title: "מחק נתוני הדגמה", body: "מחק את כל משתמשי המבחן ואת התוכן שלהם.", scan: "סרוק", commit: "מחק לצמיתות", back: "בטל", scanning: "…" },
    ru: { title: "Удалить демо-данные", body: "Удалить всех тестовых пользователей и их контент.", scan: "Проверить", commit: "Удалить безвозвратно", back: "Отмена", scanning: "…" },
  }[lang] || {
    title: "Wipe demo data", body: "Delete all test users and their content.", scan: "Scan", commit: "Delete", back: "Cancel", scanning: "…",
  };

  const scan = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/wipe-demo-data?dry_run=true");
      setPreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/wipe-demo-data?dry_run=false");
      toast.success(`Deleted ${data.deleted?.users || 0} users + related data`);
      setOpen(false);
      setPreview(null);
      window.location.reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="admin-wipe-panel">
        <div className="flex-1 min-w-0">
          <p className="text-xs tracking-[0.2em] uppercase text-red-800 mb-0.5">{L.title}</p>
          <p className="text-xs text-red-900/80">{L.body}</p>
        </div>
        <Button
          size="sm"
          onClick={() => { setOpen(true); setPreview(null); }}
          data-testid="admin-wipe-open"
          className="bg-red-700 hover:bg-red-800 text-white rounded-full text-xs h-8"
        >
          <Trash2 className="h-3 w-3 me-1" />
          {L.title}
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-red-200 max-w-lg" data-testid="admin-wipe-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-light text-red-800" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              {L.title}
            </DialogTitle>
          </DialogHeader>
          {!preview ? (
            <div className="space-y-4">
              <p className="text-sm text-[#4A4A4A]">{L.body}</p>
              <Button onClick={scan} disabled={busy} data-testid="admin-wipe-scan"
                className="w-full bg-[#B8965A] hover:bg-[#a08248] text-white rounded-full">
                {busy ? L.scanning : L.scan}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-[#4A4A4A]">
                {lang === "it" ? "Cancellerò i seguenti record" : "Will delete the following records"}:
              </p>
              <ul className="text-sm bg-red-50 border border-red-100 rounded p-3 space-y-1" data-testid="admin-wipe-preview">
                {Object.entries(preview.would_delete || {}).map(([k, v]) => (
                  <li key={k} className="flex justify-between">
                    <span className="text-[#4A4A4A]">{k}</span>
                    <span className="font-mono text-red-800">{v}</span>
                  </li>
                ))}
              </ul>
              {preview.sample?.length > 0 && (
                <details className="text-xs text-[#6B6B6B]">
                  <summary>Sample users ({preview.sample.length})</summary>
                  <ul className="mt-1 space-y-0.5">
                    {preview.sample.map((u) => (
                      <li key={u.user_id}><span className="font-mono">{u.email}</span> — {u.name}</li>
                    ))}
                  </ul>
                </details>
              )}
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)} className="text-[#6B6B6B]">{L.back}</Button>
                <Button onClick={commit} disabled={busy} data-testid="admin-wipe-commit"
                  className="bg-red-700 hover:bg-red-800 text-white rounded-full">
                  {busy ? "…" : L.commit}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function DeleteDialog({ target, onClose, onConfirm }) {
  const { lang } = useApp();
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await onConfirm(); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white border-[#E5E5E3] max-w-sm" data-testid="admin-delete-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-light text-[#1A1A1A]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            {t("confirm_delete_forever", lang)}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#6B6B6B]">"{target?.title}"</p>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-[#6B6B6B]">{t("cancel", lang)}</Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="admin-delete-confirm"
            className="bg-red-700 hover:bg-red-800 text-white rounded-full"
          >
            {busy ? "…" : t("delete", lang)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPanel() {
  const { user, lang } = useApp();
  const [props, setProps] = useState([]);
  const [members, setMembers] = useState([]);
  const [reject, setReject] = useState(null);       // {kind:'property'|'member', id, name}
  const [toDelete, setToDelete] = useState(null);   // {id, title}
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [filter, setFilter] = useState("pending");
  const [mandates, setMandates] = useState([]);
  const [mandateFilter, setMandateFilter] = useState("all"); // all | search | sale | hidden
  const [contactUnread, setContactUnread] = useState(0);
  const [allUsers, setAllUsers] = useState([]);
  const [deals, setDeals] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [memberFilter, setMemberFilter] = useState("pending"); // pending | all
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToEdit, setUserToEdit] = useState(null);
  const [userDocs, setUserDocs] = useState(null);
  const [dealToDelete, setDealToDelete] = useState(null);
  const [notifToDelete, setNotifToDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, m, mm, cs, us, ic, nf] = await Promise.all([
        api.get("/admin/properties/all"),
        api.get("/admin/members/pending"),
        api.get("/admin/mandates/all"),
        api.get("/admin/contact-submissions"),
        api.get("/admin/users"),
        api.get("/admin/intermediation"),
        api.get("/admin/notifications"),
      ]);
      setProps(p.data);
      setMembers(m.data);
      setMandates(mm.data);
      setContactUnread(cs.data.filter((s) => !s.read).length);
      setAllUsers(us.data);
      setDeals(ic.data);
      setNotifs(nf.data);
    } catch (e) { logWarn("admin load failed", e); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(
    () => (filter === "all" ? props : props.filter((p) => (p.status || "pending") === filter)),
    [props, filter],
  );
  const pendingCount = props.filter((p) => (p.status || "pending") === "pending").length;
  const rejectedCount = props.filter((p) => (p.status || "pending") === "rejected").length;

  const filteredMandates = useMemo(() => {
    if (mandateFilter === "hidden") return mandates.filter((m) => m.admin_hidden);
    if (mandateFilter === "search") return mandates.filter((m) => m.mandate_type === "search");
    if (mandateFilter === "sale") return mandates.filter((m) => m.mandate_type === "sale");
    return mandates;
  }, [mandates, mandateFilter]);
  const hiddenMandatesCount = mandates.filter((m) => m.admin_hidden).length;

  if (!user || user.role !== "admin") {
    return (
      <Card className="bg-white border-[#E5E5E3] p-8 text-center" data-testid="admin-forbidden">
        <p className="text-[#6B6B6B]">Solo l'amministratore può accedere a questo pannello.</p>
      </Card>
    );
  }

  const approveProperty = async (id) => {
    try { await api.post(`/admin/properties/${id}/approve`, {}); toast.success("Inserzione approvata"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const approveMember = async (uid) => {
    try { await api.post(`/admin/members/${uid}/approve`, {}); toast.success("Membro approvato"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const deleteMember = async () => {
    if (!userToDelete) return;
    try {
      await api.delete(`/admin/users/${userToDelete.user_id}?cascade=true`);
      toast.success(`Membro eliminato: ${userToDelete.email}`);
      setUserToDelete(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const deleteDeal = async () => {
    if (!dealToDelete) return;
    try {
      await api.delete(`/admin/intermediation/${dealToDelete.id}`);
      toast.success("Trattativa eliminata");
      setDealToDelete(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const deleteNotif = async () => {
    if (!notifToDelete) return;
    try {
      await api.delete(`/admin/notifications/${notifToDelete.id}`);
      toast.success("Notifica eliminata");
      setNotifToDelete(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const setUserStatus = async (uid, status) => {
    try {
      await api.patch(`/admin/users/${uid}`, { status });
      toast.success(`Status aggiornato: ${status}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const submitReject = async (reason) => {
    if (!reject) return;
    try {
      const REJECT_URLS = {
        property: `/admin/properties/${reject.id}/reject`,
        member:   `/admin/members/${reject.id}/reject`,
        mandate:  `/admin/mandates/${reject.id}/reject`,
      };
      await api.post(REJECT_URLS[reject.kind], { reason });
      toast.success("Rifiuto notificato");
      setReject(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const approveMandate = async (mid) => {
    try { await api.post(`/admin/mandates/${mid}/approve`, {}); toast.success("Mandato ripristinato"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/properties/${toDelete.id}`);
      toast.success("Inserzione eliminata");
      setToDelete(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const bulkDeleteRejected = async () => {
    try {
      const { data } = await api.delete("/admin/properties/rejected");
      toast.success(`${data.deleted} inserzioni rifiutate eliminate`);
      setBulkDeleteOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <Card className="bg-white border-[#E5E5E3] p-6" data-testid="admin-panel">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#B8965A] mb-1">Admin</p>
          <h3 className="text-2xl font-light text-[#1A1A1A]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            Gestione inserzioni & membri
          </h3>
        </div>
      </div>

      <Tabs defaultValue="props">
        <TabsList className="bg-[#F5F5F3] border border-[#E5E5E3] rounded-full p-1 mb-4">
          <TabsTrigger value="props" data-testid="admin-tab-props" className="rounded-full data-[state=active]:bg-[#B8965A] data-[state=active]:text-white">
            <Building2 className="me-2 h-3.5 w-3.5" />
            Inserzioni ({props.length}{pendingCount ? ` · ${pendingCount} in attesa` : ""})
          </TabsTrigger>
          <TabsTrigger value="mandates" data-testid="admin-tab-mandates" className="rounded-full data-[state=active]:bg-[#B8965A] data-[state=active]:text-white">
            <Search className="me-2 h-3.5 w-3.5" />
            Ricerche & mandati ({mandates.length}{hiddenMandatesCount ? ` · ${hiddenMandatesCount} nascoste` : ""})
          </TabsTrigger>
          <TabsTrigger value="members" data-testid="admin-tab-members" className="rounded-full data-[state=active]:bg-[#B8965A] data-[state=active]:text-white">
            <UserCheck className="me-2 h-3.5 w-3.5" />
            Membri ({members.length})
          </TabsTrigger>
          <TabsTrigger value="deals" data-testid="admin-tab-deals" className="rounded-full data-[state=active]:bg-[#B8965A] data-[state=active]:text-white">
            <Handshake className="me-2 h-3.5 w-3.5" />
            Trattative ({deals.length})
          </TabsTrigger>
          <TabsTrigger value="notifs" data-testid="admin-tab-notifs" className="rounded-full data-[state=active]:bg-[#B8965A] data-[state=active]:text-white">
            <Bell className="me-2 h-3.5 w-3.5" />
            Notifiche ({notifs.length})
          </TabsTrigger>
          <TabsTrigger value="contacts" data-testid="admin-tab-contacts" className="rounded-full data-[state=active]:bg-[#B8965A] data-[state=active]:text-white">
            <Mail className="me-2 h-3.5 w-3.5" />
            Contatti{contactUnread ? ` · ${contactUnread} nuovi` : ""}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="props" className="space-y-3">
          <div className="flex gap-2 flex-wrap items-center justify-between">
            <div className="flex gap-2 flex-wrap" data-testid="admin-prop-filters">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  data-testid={`admin-filter-${f.key}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    filter === f.key
                      ? "bg-[#B8965A] text-white border-[#B8965A]"
                      : "bg-white text-[#4A4A4A] border-[#E5E5E3] hover:bg-[#FAFAF8]"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {rejectedCount > 0 && (
              <button
                onClick={() => setBulkDeleteOpen(true)}
                data-testid="admin-bulk-delete-rejected"
                className="text-xs px-3 py-1.5 rounded-full border border-red-300 bg-white text-red-700 hover:bg-red-50 transition-colors flex items-center gap-1.5"
              >
                <Trash2 className="h-3 w-3" />
                {t("delete_all_rejected_btn", lang)} ({rejectedCount})
              </button>
            )}
          </div>

          {!filtered.length && <p className="text-sm text-[#6B6B6B] py-8 text-center">Nessuna inserzione in questa vista.</p>}
          {filtered.map((p) => {
            const status = p.status || "pending";
            const meta = STATUS_META[status] || STATUS_META.pending;
            return (
              <div key={p.id} data-testid={`admin-prop-${p.id}`} className="rounded-lg border border-[#E5E5E3] bg-[#FAFAF8] p-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[#1A1A1A]">{p.title}</p>
                  <p className="text-xs text-[#6B6B6B]">{p.city}, {p.country} · <span className="text-[#B8965A]">{formatMoney(p.price, "it")}</span></p>
                  <p className="text-[11px] text-[#8A8A8A] mt-0.5">by {p.agent_name || "—"} · {p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</p>
                  {status === "rejected" && p.rejection_reason && (
                    <p className="mt-1 text-[11px] text-red-800 bg-red-50 border border-red-200 rounded px-2 py-0.5 inline-block">
                      Motivo: {p.rejection_reason}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 items-center flex-wrap">
                  <Badge className={`text-[10px] ${meta.cls}`} data-testid={`admin-prop-status-${p.id}`}>
                    <meta.Icon className="h-3 w-3 me-1" />{meta.label}
                  </Badge>
                  {status !== "approved" && (
                    <Button size="sm" onClick={() => approveProperty(p.id)} data-testid={`admin-approve-prop-${p.id}`} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full">
                      <CheckCircle2 className="h-3.5 w-3.5 me-1.5" />Approva
                    </Button>
                  )}
                  {status !== "rejected" && (
                    <Button size="sm" onClick={() => setReject({ kind: "property", id: p.id, name: p.agent_name })} data-testid={`admin-reject-prop-${p.id}`} className="bg-transparent border border-red-300 text-red-700 hover:bg-red-50 rounded-full">
                      <XCircle className="h-3.5 w-3.5 me-1.5" />Rifiuta
                    </Button>
                  )}
                  <Button size="sm" onClick={() => setToDelete({ id: p.id, title: p.title })} data-testid={`admin-delete-prop-${p.id}`} className="bg-transparent border border-red-400 text-red-800 hover:bg-red-50 rounded-full">
                    <Trash2 className="h-3.5 w-3.5 me-1.5" />Cancella
                  </Button>
                </div>
              </div>
            );
          })}
        </TabsContent>

        <TabsContent value="mandates" className="space-y-4">
          <div className="flex gap-2 flex-wrap" data-testid="admin-mandate-filters">
            {[
              { key: "all", label: `Tutte (${mandates.length})` },
              { key: "search", label: `Ricerche (${mandates.filter(m => m.mandate_type === "search").length})` },
              { key: "sale",   label: `Vendita (${mandates.filter(m => m.mandate_type === "sale").length})` },
              { key: "hidden", label: `Nascoste (${hiddenMandatesCount})` },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setMandateFilter(f.key)}
                data-testid={`admin-mandate-filter-${f.key}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  mandateFilter === f.key
                    ? "bg-[#B8965A] text-white border-[#B8965A]"
                    : "bg-white text-[#4A4A4A] border-[#E5E5E3] hover:bg-[#FAFAF8]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <BulkTranslatePanel />

          <WipeDemoDataPanel />

          {!filteredMandates.length && (
            <p className="text-sm text-[#6B6B6B] py-8 text-center">Nessun mandato in questa vista.</p>
          )}

          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="admin-mandates-grid">
            {filteredMandates.map((m) => (
              <div key={m.id} className="relative" data-testid={`admin-mandate-${m.id}`}>
                <MandateCard
                  mandate={m}
                  onDownload={(md) => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/mandates/${md.id}/pdf`, "_blank")}
                  onChanged={load}
                  isAdmin
                />
                <div className="mt-2 flex gap-2 items-center flex-wrap">
                  {m.admin_hidden ? (
                    <Button
                      size="sm" onClick={() => approveMandate(m.id)}
                      data-testid={`admin-mandate-approve-${m.id}`}
                      className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full text-xs h-8"
                    >
                      <CheckCircle2 className="h-3 w-3 me-1" /> Ripristina
                    </Button>
                  ) : (
                    <Button
                      size="sm" onClick={() => setReject({ kind: "mandate", id: m.id, name: m.signer_name })}
                      data-testid={`admin-mandate-reject-${m.id}`}
                      className="bg-transparent border border-red-300 text-red-700 hover:bg-red-50 rounded-full text-xs h-8"
                    >
                      <EyeOff className="h-3 w-3 me-1" /> Nascondi
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="members" className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              onClick={() => setMemberFilter("pending")}
              data-testid="admin-members-filter-pending"
              className={`rounded-full text-xs h-8 ${memberFilter === "pending" ? "bg-[#B8965A] text-white" : "bg-transparent border border-[#E5E5E3] text-[#1A1A1A]"}`}
            >
              In attesa ({members.length})
            </Button>
            <Button
              size="sm"
              onClick={() => setMemberFilter("all")}
              data-testid="admin-members-filter-all"
              className={`rounded-full text-xs h-8 ${memberFilter === "all" ? "bg-[#B8965A] text-white" : "bg-transparent border border-[#E5E5E3] text-[#1A1A1A]"}`}
            >
              Tutti ({allUsers.length})
            </Button>
          </div>

          {memberFilter === "pending" && !members.length && (
            <p className="text-sm text-[#6B6B6B] py-8 text-center">Nessun membro in attesa.</p>
          )}
          {memberFilter === "pending" && members.map((m) => (
            <div key={m.user_id} data-testid={`admin-pending-member-${m.user_id}`} className="rounded-lg border border-[#E5E5E3] bg-[#FAFAF8] p-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#1A1A1A]">{m.name}</p>
                <p className="text-xs text-[#6B6B6B] font-mono">{m.email}</p>
                <p className="text-[11px] text-[#8A8A8A] mt-0.5">{m.role} · {m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}</p>
              </div>
              <div className="flex gap-2">
                <Badge className="bg-amber-100 text-amber-800 border border-amber-200"><Clock className="h-3 w-3 me-1" />PENDING</Badge>
                <Button size="sm" onClick={() => approveMember(m.user_id)} data-testid={`admin-approve-member-${m.user_id}`} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full">
                  <CheckCircle2 className="h-3.5 w-3.5 me-1.5" />Approva
                </Button>
                <Button size="sm" onClick={() => setReject({ kind: "member", id: m.user_id, name: m.name })} data-testid={`admin-reject-member-${m.user_id}`} className="bg-transparent border border-red-300 text-red-700 hover:bg-red-50 rounded-full">
                  <XCircle className="h-3.5 w-3.5 me-1.5" />Rifiuta
                </Button>
              </div>
            </div>
          ))}

          {memberFilter === "all" && !allUsers.length && (
            <p className="text-sm text-[#6B6B6B] py-8 text-center">Nessun membro registrato.</p>
          )}
          {memberFilter === "all" && allUsers.map((m) => {
            const isSelf = m.user_id === user.user_id;
            const status = m.role === "admin" ? "ADMIN" : (m.agent_approved ? "APPROVATO" : "PENDING");
            const acctStatus = (m.status || "active").toLowerCase();
            const statusColorMap = {
              active: "bg-emerald-100 text-emerald-800 border border-emerald-200",
              standby: "bg-amber-100 text-amber-800 border border-amber-200",
              inactive: "bg-red-100 text-red-800 border border-red-200",
            };
            return (
              <div key={m.user_id} data-testid={`admin-all-member-${m.user_id}`} className="rounded-lg border border-[#E5E5E3] bg-white p-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[#1A1A1A]">{m.name} {isSelf && <span className="text-[10px] text-[#B8965A]">(tu)</span>}</p>
                  <p className="text-xs text-[#6B6B6B] font-mono">{m.email}</p>
                  <p className="text-[11px] text-[#8A8A8A] mt-0.5">
                    {m.role} · {m.ndnca_signed ? "NDNCA ✓" : "NDNCA ✗"} · {m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}
                  </p>
                </div>
                <div className="flex gap-2 items-center flex-wrap">
                  <Badge className={
                    status === "ADMIN" ? "bg-[#B8965A] text-white"
                    : status === "APPROVATO" ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                    : "bg-amber-100 text-amber-800 border border-amber-200"
                  }>{status}</Badge>
                  <Badge className={statusColorMap[acctStatus] || statusColorMap.active} data-testid={`admin-member-status-${m.user_id}`}>
                    {acctStatus.toUpperCase()}
                  </Badge>
                  <Button
                    size="sm" onClick={() => setUserDocs(m)}
                    data-testid={`admin-member-docs-${m.user_id}`}
                    className="bg-transparent border border-[#B8965A]/40 text-[#B8965A] hover:bg-[#F5EDDA] rounded-full h-8"
                    title="Documenti firmati"
                  >
                    <Folder className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm" onClick={() => setUserToEdit(m)}
                    data-testid={`admin-member-edit-${m.user_id}`}
                    className="bg-transparent border border-[#B8965A]/40 text-[#B8965A] hover:bg-[#F5EDDA] rounded-full h-8"
                    title="Modifica"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  {!isSelf && acctStatus !== "active" && (
                    <Button
                      size="sm" onClick={() => setUserStatus(m.user_id, "active")}
                      data-testid={`admin-member-activate-${m.user_id}`}
                      className="bg-transparent border border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-full h-8"
                      title="Attiva"
                    >
                      <Power className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {!isSelf && acctStatus !== "standby" && (
                    <Button
                      size="sm" onClick={() => setUserStatus(m.user_id, "standby")}
                      data-testid={`admin-member-standby-${m.user_id}`}
                      className="bg-transparent border border-amber-300 text-amber-700 hover:bg-amber-50 rounded-full h-8"
                      title="Standby"
                    >
                      <Pause className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {!isSelf && acctStatus !== "inactive" && (
                    <Button
                      size="sm" onClick={() => setUserStatus(m.user_id, "inactive")}
                      data-testid={`admin-member-deactivate-${m.user_id}`}
                      className="bg-transparent border border-red-300 text-red-700 hover:bg-red-50 rounded-full h-8"
                      title="Disattiva"
                    >
                      <ToggleRight className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {!isSelf && (
                    <Button
                      size="sm"
                      onClick={() => setUserToDelete(m)}
                      data-testid={`admin-delete-member-${m.user_id}`}
                      className="bg-transparent border border-red-400 text-red-800 hover:bg-red-50 rounded-full h-8"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}

          <Dialog open={!!userToDelete} onOpenChange={(v) => !v && setUserToDelete(null)}>
            <DialogContent className="bg-white border-red-200 max-w-lg overflow-visible" data-testid="admin-delete-member-dialog" aria-describedby="admin-delete-member-desc">
              <DialogHeader>
                <DialogTitle className="text-2xl font-light text-red-800" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                  Eliminare il membro?
                </DialogTitle>
              </DialogHeader>
              {userToDelete && (
                <div id="admin-delete-member-desc" className="space-y-2 py-2">
                  <p className="text-sm text-[#4A4A4A]" data-testid="admin-delete-member-info">
                    Verrà eliminato in modo permanente <b>{userToDelete.name}</b>{" "}
                    <span className="font-mono text-[#6B6B6B]">({userToDelete.email})</span> e tutti i suoi contenuti:
                    inserzioni, mandati, contratti, notifiche, sessioni.
                  </p>
                  <p className="text-xs text-red-700">L'operazione è irreversibile.</p>
                </div>
              )}
              <DialogFooter>
                <Button variant="ghost" onClick={() => setUserToDelete(null)} className="text-[#6B6B6B]">Annulla</Button>
                <Button onClick={deleteMember} data-testid="admin-delete-member-confirm" className="bg-red-700 hover:bg-red-800 text-white rounded-full">
                  <Trash2 className="h-3.5 w-3.5 me-1.5" />Elimina definitivamente
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="deals" className="space-y-3" data-testid="admin-deals-list">
          {!deals.length && <p className="text-sm text-[#6B6B6B] py-8 text-center">Nessuna trattativa.</p>}
          {deals.map((d) => (
            <div key={d.id} data-testid={`admin-deal-${d.id}`} className="rounded-lg border border-[#E5E5E3] bg-[#FAFAF8] p-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#1A1A1A] truncate">{d.property_title} — {d.property_city}</p>
                <p className="text-xs text-[#6B6B6B]">
                  Agente: <span className="font-mono">{d.agent_email || d.agent_id}</span> · Buyer: <span className="font-mono">{d.buyer_email || d.buyer_user_id}</span>
                </p>
                <p className="text-[11px] text-[#8A8A8A] mt-0.5 font-mono">{d.id.slice(0, 8).toUpperCase()} · {d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}</p>
              </div>
              <div className="flex gap-2 items-center">
                <Badge className={d.buyer_signed ? "bg-emerald-100 text-emerald-800 border border-emerald-200" : "bg-amber-100 text-amber-800 border border-amber-200"}>
                  {d.buyer_signed ? "FIRMATO" : "IN ATTESA"}
                </Badge>
                <Button size="sm" onClick={() => setDealToDelete(d)} data-testid={`admin-deal-delete-${d.id}`}
                        className="bg-transparent border border-red-300 text-red-700 hover:bg-red-50 rounded-full text-xs h-8">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
          <Dialog open={!!dealToDelete} onOpenChange={(v) => !v && setDealToDelete(null)}>
            <DialogContent className="bg-white border-red-200 max-w-md" data-testid="admin-deal-delete-dialog" aria-describedby="admin-deal-delete-desc">
              <DialogHeader><DialogTitle className="text-2xl font-light text-red-800" style={{ fontFamily: "Cormorant Garamond, serif" }}>Eliminare la trattativa?</DialogTitle></DialogHeader>
              {dealToDelete && (
                <p id="admin-deal-delete-desc" className="text-sm text-[#4A4A4A] py-2">
                  Verrà eliminato il contratto <b>{dealToDelete.id.slice(0, 8).toUpperCase()}</b> tra{" "}
                  <span className="font-mono">{dealToDelete.agent_email}</span> e{" "}
                  <span className="font-mono">{dealToDelete.buyer_email}</span>. L'operazione è irreversibile.
                </p>
              )}
              <DialogFooter>
                <Button variant="ghost" onClick={() => setDealToDelete(null)} className="text-[#6B6B6B]">Annulla</Button>
                <Button onClick={deleteDeal} data-testid="admin-deal-delete-confirm" className="bg-red-700 hover:bg-red-800 text-white rounded-full">
                  <Trash2 className="h-3.5 w-3.5 me-1.5" />Elimina definitivamente
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="notifs" className="space-y-3" data-testid="admin-notifs-list">
          {!notifs.length && <p className="text-sm text-[#6B6B6B] py-8 text-center">Nessuna notifica registrata.</p>}
          {notifs.slice(0, 200).map((n) => (
            <div key={n.id} data-testid={`admin-notif-${n.id}`} className="rounded-lg border border-[#E5E5E3] bg-[#FAFAF8] p-3 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#1A1A1A] truncate">{n.subject}</p>
                <p className="text-xs text-[#6B6B6B] line-clamp-2">{n.body}</p>
                <p className="text-[11px] text-[#8A8A8A] mt-1 font-mono">
                  {n.user_id} · {n.created_at ? new Date(n.created_at).toLocaleString() : ""} · {n.read ? "letta" : "non letta"}
                </p>
              </div>
              <Button size="sm" onClick={() => setNotifToDelete(n)} data-testid={`admin-notif-delete-${n.id}`}
                      className="bg-transparent border border-red-300 text-red-700 hover:bg-red-50 rounded-full text-xs h-8">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
          <Dialog open={!!notifToDelete} onOpenChange={(v) => !v && setNotifToDelete(null)}>
            <DialogContent className="bg-white border-red-200 max-w-md" data-testid="admin-notif-delete-dialog" aria-describedby="admin-notif-delete-desc">
              <DialogHeader><DialogTitle className="text-2xl font-light text-red-800" style={{ fontFamily: "Cormorant Garamond, serif" }}>Eliminare la notifica?</DialogTitle></DialogHeader>
              {notifToDelete && (
                <p id="admin-notif-delete-desc" className="text-sm text-[#4A4A4A] py-2">
                  Verrà rimossa: "<b>{notifToDelete.subject}</b>". L'operazione è irreversibile.
                </p>
              )}
              <DialogFooter>
                <Button variant="ghost" onClick={() => setNotifToDelete(null)} className="text-[#6B6B6B]">Annulla</Button>
                <Button onClick={deleteNotif} data-testid="admin-notif-delete-confirm" className="bg-red-700 hover:bg-red-800 text-white rounded-full">
                  <Trash2 className="h-3.5 w-3.5 me-1.5" />Elimina
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="contacts">
          <ContactInbox />
        </TabsContent>
      </Tabs>

      <RejectDialog open={!!reject} target={reject} onClose={() => setReject(null)} onSubmit={submitReject} />
      <DeleteDialog target={toDelete} onClose={() => setToDelete(null)} onConfirm={confirmDelete} />

      <AdminUserEditDialog
        target={userToEdit}
        onClose={() => setUserToEdit(null)}
        onSaved={() => { setUserToEdit(null); load(); }}
      />
      <AdminUserDocsDialog target={userDocs} onClose={() => setUserDocs(null)} />

      <Dialog open={bulkDeleteOpen} onOpenChange={(v) => !v && setBulkDeleteOpen(false)}>
        <DialogContent className="bg-white border-[#E5E5E3] max-w-sm" data-testid="admin-bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-light text-[#1A1A1A]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              {t("delete_all_rejected", lang)}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#6B6B6B]">
            <b>{rejectedCount}</b>
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulkDeleteOpen(false)} className="text-[#6B6B6B]">{t("cancel", lang)}</Button>
            <Button
              onClick={bulkDeleteRejected}
              data-testid="admin-bulk-delete-confirm"
              className="bg-red-700 hover:bg-red-800 text-white rounded-full"
            >
              {t("delete_all", lang)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
